<template>
  <form class="voting-vote-section__form" @submit.prevent="onSubmit">
    <div
      v-for="(mainCategory, mainCategoryName) in nonEmptyCategories"
      :key="mainCategoryName"
      class="voting-vote-section__form-main-category-wrapper"
    >
      <h3 class="voting-vote-section__form-main-category-title">
        {{ getCategoryTitle(mainCategoryName) }}
      </h3>

      <div
        v-for="(subCategory, subCategoryName) in mainCategory"
        :key="subCategoryName"
        class="voting-vote-section__form-category-wrapper"
      >
        <div class="voting-vote-section__form-header">
          <h2 class="title-new uppercase">
            {{ formatCategoryName(subCategoryName) }}
          </h2>
          <p class="voting-vote-section__form-subtitle">
            {{ getCategorySubTitle(mainCategoryName) }}
          </p>
        </div>
        <div class="voting-vote-section__form-brands">
          <div
            class="voting-vote-section__form-brands-item"
            :class="brandDynamicClass(subCategoryName, brand.Id)"
            v-for="brand in subCategory.Brands"
            :key="brand.Id"
          >
            <div class="voting-vote-section__form-brands-header">
              <p class="voting-vote-section__form-brands-name">
                {{ brand.Name }}
              </p>
              <a-image
                :src="brand.LogoUrl"
                :alt="brand.Name"
                class="voting-vote-section__form-brands-img"
              />
            </div>
            <div
              v-for="nomination in brand.Nominations"
              :key="nomination.NomineeId"
              class="voting-vote-section__form-rewarding-categories-wrapper"
            >
              <div class="voting-vote-section__form-rewarding-categories">
                <div>
                  <label
                    class="voting-vote-section__form-label"
                    :class="
                      dynamicLabelClass(
                        nomination.Category.Id,
                        brand.Id,
                        subCategoryName
                      )
                    "
                    :for="
                      `${subCategoryName}-${brand.Id}-${nomination.Category.Id}`
                    "
                  >
                    {{ nomination.Category.Name }}</label
                  >
                  <p
                    v-if="
                      isCategoryAlreadyVoted(
                        nomination.Category.Id,
                        brand.Id,
                        subCategoryName
                      )
                    "
                    class="voting-vote-section__form-already-selected"
                  >
                    You selected
                    {{
                      nomineeIdToBrandName[
                        votesByCategory[nomination.Category.Id]
                      ]
                    }}. Change selection?
                  </p>
                </div>

                <client-only>
                  <div
                    v-if="isLoggedIn"
                    class="voting-vote-section__form-radio-wrapper"
                  >
                    <input
                      v-model="voteData[`${subCategoryName}-${brand.Id}`]"
                      type="radio"
                      :id="
                        `${subCategoryName}-${brand.Id}-${nomination.Category.Id}`
                      "
                      :name="`${subCategoryName}-${brand.Id}`"
                      :value="nomination.NomineeId"
                      class="voting-vote-section__form-input"
                      @change="
                        handleVoteChange(
                          nomination.Category.Id,
                          nomination.NomineeId,
                          brand.Name
                        )
                      "
                    />

                    <label
                      :for="
                        `${subCategoryName}-${brand.Id}-${nomination.Category.Id}`
                      "
                      class="voting-vote-section__form-radio-icon"
                    >
                      <a-icon
                        :icon="
                          getRadioIcon(
                            subCategoryName,
                            brand.Id,
                            nomination.NomineeId
                          )
                        "
                        original
                        fill="none"
                      />
                    </label>
                  </div>
                </client-only>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <voting-nominations-error-message v-if="wasErrorOnCategoriesRequest" />
    <div v-if="showSubmitButton" class="voting-vote-section__form-button">
      <a-main-button
        type="submit"
        :disabled="isSubmitting"
        class="voting-vote-section__form-btn"
      >
        Submit
      </a-main-button>
      <a-error-message
        :bold="true"
        class="voting-vote-section__form-error-msg"
        :error="serverError"
      />
    </div>
  </form>
</template>

<script>
import { PROP_TYPES, propValidator } from '@fmpedia/validators'
import { MODAL } from 'modals/AModalWrapper/enums'
import { ELEMENT_ID, ELEMENT_SELECTOR_BY_ELEMENT_ID } from 'enums/element-id'
import AMainButton from 'shared/AMainButton/index'
import { ROUTE, ROUTE_NAME } from 'enums/routes'
import { mapActions, mapGetters } from 'vuex'
import { VOTING_CATEGORY_TITLE_DATA_BY_CATEGORY_TYPE } from 'sections/_voting/VotingVoteSection/VotingVoteForm/enums'
import AErrorMessage from 'shared/AErrorMessage/index.vue'
import AIcon, { ICON } from 'shared/AIcon/index.vue'
import VotingNominationsErrorMessage from 'sections/_voting/VotingNominations/VotingNominationsErrorMessage'
import { formatVoteKey } from 'sections/_voting/VotingVoteSection/VotingVoteForm/helpers'
import { IS_VOTING_CLOSED } from 'enums/main-route'

export default {
  name: 'VotingVoteForm',
  components: {
    AMainButton,
    AErrorMessage,
    AIcon,
    VotingNominationsErrorMessage
  },
  props: {
    categoriesResponse: propValidator([PROP_TYPES.OBJECT]),
    wasAuthorizedDataReceived: propValidator([PROP_TYPES.BOOLEAN]),
    inProgress: propValidator([PROP_TYPES.BOOLEAN]),
    wasErrorOnCategoriesRequest: propValidator([PROP_TYPES.BOOLEAN])
  },
  consts: {
    VOTE_FORM_TO: {
      path: ROUTE.VOTING,
      hash: ELEMENT_SELECTOR_BY_ELEMENT_ID[ELEMENT_ID.VOTE_FORM]
    },
    ICON,
    IS_VOTING_CLOSED
  },
  data() {
    return {
      isDisabled: true,
      voteData: {},
      votesByCategory: {},
      isSubmitting: false,
      isIncomplete: false,
      nomineeIdToBrandName: {},
      serverError: null
    }
  },
  computed: {
    ...mapGetters({
      isLoggedIn: 'auth/isLoggedIn',
      userId: 'auth/userId'
    }),
    nonEmptyCategories() {
      const categories = {}
      for (const key in this.categoriesResponse) {
        if (Object.keys(this.categoriesResponse[key]).length) {
          categories[key] = this.categoriesResponse[key]
        }
      }
      return categories
    },
    brandDynamicClass() {
      return (subCategoryName, brandId) => ({
        'voting-vote-section__form-brands-item--error':
          this.isIncomplete && !this.voteData[`${subCategoryName}-${brandId}`]
      })
    },
    showSubmitButton() {
      return !IS_VOTING_CLOSED
    }
  },
  validations() {
    return {
      voteData: {
        hasAtLeastOneVote: value => {
          return this.isMinimumVote(value)
        }
      }
    }
  },
  methods: {
    ...mapActions({
      requestVote: 'categories/requestVote'
    }),
    getCategoryTitle(categoryName) {
      return VOTING_CATEGORY_TITLE_DATA_BY_CATEGORY_TYPE[categoryName].title
    },
    getCategorySubTitle(categoryName) {
      return VOTING_CATEGORY_TITLE_DATA_BY_CATEGORY_TYPE[categoryName].subtitle
    },
    // TODO: discuss with Backend as we shouldn't process backend variables for display purposes
    formatCategoryName(name) {
      return name.replace(/([A-Z])/g, ' $1').trim()
    },
    handleVoteChange(categoryId, nomineeId, brandName) {
      this.nomineeIdToBrandName[nomineeId] = brandName
      const previousNomineeId = this.votesByCategory[categoryId]

      if (previousNomineeId && previousNomineeId !== nomineeId) {
        const previousVoteKey = this.findVoteKeyByNominee(previousNomineeId)

        if (previousVoteKey) {
          this.$delete(this.voteData, previousVoteKey)
        }
      }

      this.votesByCategory[categoryId] = nomineeId

      if (this.isIncomplete && this.isMinimumVote(this.voteData)) {
        this.isIncomplete = false
      }
    },
    getRadioIcon(subCategoryName, brandId, nomineeId) {
      const isSelected =
        this.voteData[formatVoteKey(subCategoryName, brandId)] === nomineeId

      return isSelected
        ? this.$options.consts.ICON.RADIO_BUTTON_CHECKED
        : this.$options.consts.ICON.RADIO_BUTTON_DEFAULT
    },
    findVoteKeyByNominee(nomineeId) {
      for (const currentNomineeId in this.voteData) {
        if (this.voteData[currentNomineeId] === nomineeId) {
          return currentNomineeId
        }
      }
      return null
    },
    isCategoryAlreadyVoted(categoryId, currentBrandId, currentSubCategoryName) {
      const votedNomineeId = this.votesByCategory[categoryId]
      if (!votedNomineeId) return false

      const currentVoteKey = formatVoteKey(
        currentSubCategoryName,
        currentBrandId
      )

      return Object.entries(this.voteData).some(
        ([voteKey, nomineeId]) =>
          nomineeId === votedNomineeId && !voteKey.includes(currentVoteKey)
      )
    },
    dynamicLabelClass(categoryId, brandId, subCategoryName) {
      return {
        'voting-vote-section__form-label--selected': this.isCategoryAlreadyVoted(
          categoryId,
          brandId,
          subCategoryName
        )
      }
    },
    isMinimumVote(voteData) {
      return Object.keys(voteData).length >= 1
    },
    async onSubmit() {
      if (!this.isLoggedIn) {
        this.$scrollTo(this.$options.consts.VOTE_FORM_TO.hash)
        this.$helper.openModal(MODAL.AUTH)
        return
      }

      if (this.$helper.isValidationFailed(this)) {
        this.$scrollTo(this.$options.consts.VOTE_FORM_TO.hash)
        this.isIncomplete = true
        return
      }

      const selectedNomineeIds = Object.values(this.voteData)

      try {
        this.disableSubmitButton()

        const payload = {
          NomineeIds: selectedNomineeIds
        }
        await this.requestVote({ payload })

        await this.$router.push({
          name: ROUTE_NAME.THANK_YOU
        })
      } catch (err) {
        this.serverError = err
        this.$errorHandler(err, this, { showMessage: false })
      } finally {
        this.enableSubmitButton()
      }
    },
    disableSubmitButton() {
      this.isSubmitting = true
    },
    enableSubmitButton() {
      this.isSubmitting = false
    }
  }
}
</script>

<style lang="scss" scoped>
.voting-vote-section__form {
  .voting-vote-section__form-main-category-title {
    color: $c--white;
    font-style: normal;
    font-weight: 400;
    text-transform: uppercase;

    $margin-bottom: (
      $above-desktop-xl: 80px,
      $mobile: 48px
    );

    $font-size: (
      $above-desktop-xl: 48px,
      $tablet: 32px,
      $mobile: 24px
    );

    $line-height: (
      $above-desktop-xl: 60px,
      $tablet: 32px
    );

    @include setStyleByBreakpoint('margin-bottom', $margin-bottom);
    @include setStyleByBreakpoint('font-size', $font-size);
    @include setStyleByBreakpoint('line-height', $line-height);

    &:before {
      margin-bottom: 33px;
    }

    &:after {
      margin-top: 33px;
    }

    &:before,
    &:after {
      content: '';
      background: linear-gradient(
        270deg,
        #1a15cb 0%,
        #8641e9 50%,
        #88e4fb 100%
      );
      height: 2px;
      width: 100%;
      display: block;
    }
  }

  .voting-vote-section__form-category-wrapper {
    $margin-bottom: (
      $above-desktop-xl: 128px,
      $mobile: 64px
    );

    @include setStyleByBreakpoint('margin-bottom', $margin-bottom);
  }

  .voting-vote-section__form-header {
    justify-content: space-between;

    border-bottom: 1px solid $c--gray;
    display: flex;

    $align-items: (
      $above-desktop-xl: center,
      $mobile: flex-start
    );

    $flex-direction: (
      $above-desktop-xl: row,
      $mobile: column
    );

    $padding-bottom: (
      $above-desktop-xl: 32px
    );

    @include setStyleByBreakpoint('align-items', $align-items);
    @include setStyleByBreakpoint('flex-direction', $flex-direction);
    @include setStyleByBreakpoint('padding-bottom', $padding-bottom);
  }

  .voting-vote-section__form-subtitle {
    color: $c--white;
    font-size: 18px;
    font-weight: 400;
    line-height: 28px;

    @include mobile {
      margin-top: 10px;
    }
  }

  .voting-vote-section__form-brands {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 32px;
    margin-top: 32px;

    @include tablet {
      grid-template-columns: repeat(2, 1fr);
    }

    @include mobile {
      grid-template-columns: 1fr;
    }
  }

  .voting-vote-section__form-brands-item {
    background: $c--background-dark-7;
    border: 2px solid $c--background-dark-7;
    border-radius: 12px;
    padding: 32px;

    &:nth-child(5n + 1),
    &:nth-child(5n + 2),
    &:nth-child(5n + 3) {
      grid-column: span 2;
    }

    &:nth-child(5n + 4),
    &:nth-child(5n + 5) {
      grid-column: span 3;
    }

    @include tablet {
      padding: 24px;

      grid-column: span 1;

      &:nth-child(3n + 1),
      &:nth-child(3n + 2) {
        grid-column: span 1;
      }

      &:nth-child(3n + 3) {
        grid-column: span 2;
      }
    }

    @include mobile {
      grid-column: span 1;

      &:nth-child(3n + 1),
      &:nth-child(3n + 2),
      &:nth-child(3n + 3) {
        grid-column: span 1;
      }
    }

    &--error {
      border: 2px solid $c--error;
    }
  }

  .voting-vote-section__form-brands-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    @include mobile {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .voting-vote-section__form-brands-img {
    margin-left: 5px;
    border-radius: 4px;
    border: 1px solid rgba($c--white, 0.1);
    width: 128px;
  }

  .voting-vote-section__form-brands-name {
    color: $c--white;
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;

    @include mobile {
      margin-bottom: 16px;
    }
  }

  .voting-vote-section__form-rewarding-categories-wrapper {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .voting-vote-section__form-rewarding-categories {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .voting-vote-section__form-label {
    color: $c--white;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 28px;

    &--selected {
      opacity: 0.3;
    }
  }

  .voting-vote-section__form-input {
    display: none;
  }

  .voting-vote-section__form-radio-wrapper {
    margin-left: 12px;
    flex-shrink: 0;
  }

  .voting-vote-section__form-radio-icon {
    cursor: pointer;
  }

  .voting-vote-section__form-already-selected {
    font-size: 14px;
    font-style: italic;
    line-height: 16px;
  }

  .voting-vote-section__form-button {
    padding-bottom: 50px;
  }

  .voting-vote-section__form-btn {
    border-radius: 80px;
    background: linear-gradient(270deg, #1a15cb 0%, #8641e9 50%, #88e4fb 100%);
    padding: 13px 26px;
    width: 100%;
    color: $c--white;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: 22px;
    letter-spacing: 2px;
    text-transform: uppercase;
    text-decoration: none;

    &:hover:not(.disabled) {
      background-image: none;
      background: linear-gradient(
        270deg,
        #1a15cb 0%,
        #8641e9 50%,
        #88e4fb 100%
      );
    }
  }

  .voting-vote-section__form-error-msg {
    margin-top: 10px;
    text-align: center;
  }
}
</style>
